import type { Effects, InvokeResult, ReduxSagaModelEffects } from '@/models/types';
import type { Column, SqlQueryResults, SqlQueryRowCountResults } from '@/services/types';
import type { VisualizationTypeAndConfiguration } from '@/services/visualization';
import Dataset from '@/services/dataset';
import Visualization from '@/services/visualization';
import { DATASET_PREVIEW_LIMIT, DEFAULT_VISUALIZATION_LIMIT } from '@/utils/constants';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { handleUserError } from '@/utils/handleError';
import { assertIsNumber } from '@/utils/typeChecks';
import {
  cancelRedshiftQueryAndIgnoreError,
  waitForRedshiftQueryUntilDoneAndReturnResults,
} from '@/services/dataset.common';

const ns = 'app.models.common.preview';

export type State = {
  previewRowCount: InvokeResult<SqlQueryRowCountResults> | null;
  previewColumns: Pick<Column, 'id'>[]; // only id is needed for now for the column picker
  invokePreviewResult: InvokeResult<SqlQueryResults> | null;
  invokeVizPreviewResult: InvokeResult<{
    vizTypeAndConfigurations: VisualizationTypeAndConfiguration;
    sqlQueryResults: SqlQueryResults;
  }> | null;
};

export const initialState: State = {
  previewRowCount: null,
  previewColumns: [],
  invokePreviewResult: null,
  invokeVizPreviewResult: null,
};

export type SavePreviewRowCountAction = {
  type: 'savePreviewRowCount';
  payload: Pick<State, 'previewRowCount'>;
};

export type SavePreviewColumnsAction = {
  type: 'savePreviewColumns';
  payload: Pick<State, 'previewColumns'>;
};

export type SaveInvokePreviewResultAction = {
  type: 'saveInvokePreviewResult';
  payload: Pick<State, 'invokePreviewResult'>;
};

export type SaveInvokeVizPreviewResultAction = {
  type: 'saveInvokeVizPreviewResult';
  payload: Pick<State, 'invokeVizPreviewResult'>;
};

export type ReduxSagaModelActions = {
  savePreviewRowCount: SavePreviewRowCountAction;
  savePreviewColumns: SavePreviewColumnsAction;
  saveInvokePreviewResult: SaveInvokePreviewResultAction;
  saveInvokeVizPreviewResult: SaveInvokeVizPreviewResultAction;
};

export type InvokePreviewEffectPayload = {
  code: string;
  isLimited: boolean;
  pageSize: number;
  currentPage?: number;
};

export type InvokeVizPreviewEffectPayload = {
  typeAndConfigurations: VisualizationTypeAndConfiguration;
};

const executionIdByPreviewRequestIdMap: Record<string, string> = {};

function* invokePreviewWorker(
  { payload }: { payload: InvokePreviewEffectPayload },
  { put, cancelled, spawn }: Effects,
): Generator<any, void, any> {
  const requestId = Date.now().toString();
  const onSuccess = (executionId: string) => {
    executionIdByPreviewRequestIdMap[requestId] = executionId;
  };
  try {
    let rowCount: number;
    if (payload.isLimited) {
      rowCount = DATASET_PREVIEW_LIMIT;
    } else {
      const rowCountExecutionId = yield* sgcall(() =>
        Dataset.testSqlQuery(payload.code, 0, 0, [], [], onSuccess),
      );
      const { rows: rowCountRows } = yield* sgcall(() =>
        waitForRedshiftQueryUntilDoneAndReturnResults(rowCountExecutionId),
      );
      const temp1 = rowCountRows[0]?.count;
      assertIsNumber(temp1, 'BEEM250121152530');
      rowCount = temp1;
    }
    yield put<SavePreviewRowCountAction>({
      type: 'savePreviewRowCount',
      payload: { previewRowCount: { success: true, data: { count: rowCount } } },
    });

    const queryExecutionId = yield* sgcall(() =>
      Dataset.testSqlQuery(
        payload.code,
        payload.isLimited ? DATASET_PREVIEW_LIMIT : payload.pageSize,
        ((payload.currentPage ?? 1) - 1) * payload.pageSize,
        [],
        [],
        onSuccess,
      ),
    );
    const t1 = yield* sgcall(() => waitForRedshiftQueryUntilDoneAndReturnResults(queryExecutionId));
    const rs = t1.rows;

    const previewColumns = rs.length > 0 ? Object.keys(rs[0]).map((el) => ({ id: el })) : [];
    yield put<SavePreviewColumnsAction>({
      type: 'savePreviewColumns',
      payload: { previewColumns },
    });

    const data: SqlQueryResults = rs.map((el, i) => ({ _beem_row_number: i + 1, ...el }));
    yield put<SaveInvokePreviewResultAction>({
      type: 'saveInvokePreviewResult',
      payload: { invokePreviewResult: { success: true, data } },
    });
  } catch (e) {
    yield put<SaveInvokePreviewResultAction>({
      type: 'saveInvokePreviewResult',
      payload: {
        invokePreviewResult: {
          success: false,
          error: handleUserError(e, `${ns}.invokePreview.error`),
        },
      },
    });
  } finally {
    if (yield cancelled()) {
      yield spawn(function* cancelQueryTask() {
        while (!executionIdByPreviewRequestIdMap[requestId]) {
          yield* sgcall(() => new Promise((resolve) => setTimeout(resolve, 1000)));
        }
        yield* sgcall(() =>
          cancelRedshiftQueryAndIgnoreError({
            executionId: executionIdByPreviewRequestIdMap[requestId],
          }),
        );
      });
    }
  }
}

// to prevent unnecessary calls to the backend when the viz preview is invoked multiple times for the same viz query
let cachedValueVizQuery = '';
let cachedValueOfSqlQueryResults = '';

const executionIdByVizPreviewRequestIdMap: Record<string, string> = {};

function* invokeVizPreviewWorker(
  { payload }: { payload: InvokeVizPreviewEffectPayload },
  { put, cancelled, spawn }: Effects,
): Generator<any, void, any> {
  const requestId = Date.now().toString();
  const onSuccess = (executionId: string) => {
    executionIdByVizPreviewRequestIdMap[requestId] = executionId;
  };
  try {
    const { typeAndConfigurations } = payload;
    const sqlQuery = yield* sgselect((s) => s.dataset.sqlQuery, 'BEEM250117074115');
    const isSqlEditorLimitEnabled = yield* sgselect((s) => s.dataset.isSqlEditorLimitEnabled);
    const { vizQuery, wrapperQuery, displayColumns } = Visualization.getVisualizationQuery(
      typeAndConfigurations,
      sqlQuery,
      isSqlEditorLimitEnabled,
    );

    if (cachedValueOfSqlQueryResults && cachedValueVizQuery === wrapperQuery + vizQuery) {
      yield put<SaveInvokeVizPreviewResultAction>({
        type: 'saveInvokeVizPreviewResult',
        payload: {
          invokeVizPreviewResult: {
            success: true,
            data: {
              vizTypeAndConfigurations: typeAndConfigurations,
              sqlQueryResults: JSON.parse(cachedValueOfSqlQueryResults),
            },
          },
        },
      });
      return;
    }
    cachedValueOfSqlQueryResults = '';
    cachedValueVizQuery = wrapperQuery + vizQuery;

    const queryExecutionId = yield* sgcall(() =>
      Dataset.testSqlQuery(
        vizQuery,
        DEFAULT_VISUALIZATION_LIMIT,
        0,
        [],
        [],
        onSuccess,
        wrapperQuery,
        displayColumns,
      ),
    );
    const rs = yield* sgcall(() => waitForRedshiftQueryUntilDoneAndReturnResults(queryExecutionId));
    cachedValueOfSqlQueryResults = JSON.stringify(rs.rows);
    yield put<SaveInvokeVizPreviewResultAction>({
      type: 'saveInvokeVizPreviewResult',
      payload: {
        invokeVizPreviewResult: {
          success: true,
          data: { vizTypeAndConfigurations: typeAndConfigurations, sqlQueryResults: rs.rows },
        },
      },
    });
  } catch (e) {
    yield put<SaveInvokeVizPreviewResultAction>({
      type: 'saveInvokeVizPreviewResult',
      payload: {
        invokeVizPreviewResult: {
          success: false,
          error: handleUserError(e, `${ns}.invokeVizPreview.error`),
        },
      },
    });
  } finally {
    if (yield cancelled()) {
      yield spawn(function* cancelQueryTask() {
        while (!executionIdByVizPreviewRequestIdMap[requestId]) {
          yield* sgcall(() => new Promise((resolve) => setTimeout(resolve, 1000)));
        }
        yield* sgcall(() =>
          cancelRedshiftQueryAndIgnoreError({
            executionId: executionIdByVizPreviewRequestIdMap[requestId],
          }),
        );
      });
    }
  }
}

export function generateEffectsForPreviewCompatibleType(): ReduxSagaModelEffects {
  return {
    invokePreview: [
      function* invokePreviewGenerator(
        args: { payload: InvokePreviewEffectPayload },
        effects: Effects,
      ) {
        yield effects.call(invokePreviewWorker, args, effects);
      },
      { type: 'takeLatest' },
    ],
    invokeVizPreview: [
      function* invokeVizPreviewGenerator(
        args: { payload: InvokeVizPreviewEffectPayload },
        effects: Effects,
      ) {
        yield effects.call(invokeVizPreviewWorker, args, effects);
      },
      { type: 'takeLatest' },
    ],
  };
}
